import React, { useState, useEffect } from 'react';
import { useUserStore } from '../stores/useUserStore';
import { useNotification } from '../hooks/useNotification';
import { useTranslation } from '../hooks/useTranslation';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Star,
  Info,
  CheckCircle,
  Clock,
  Target,
  Trophy,
  Zap,
  Heart,
  Shield,
  Award,
  TrendingUp,
  Users,
  Calendar,
  Coffee,
} from 'lucide-react';

interface PointTask {
  id: string;
  title: string;
  description: string;
  points: number;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  icon: React.ComponentType<any>;
  completed: boolean;
  progress?: number;
  maxProgress?: number;
  explanation: string;
  requiresApproval?: boolean; // Whether this task requires approval/verification
  approvalStatus?: 'pending' | 'approved' | 'rejected'; // Approval status if requires approval
}

interface PointSystemInfo {
  title: string;
  description: string;
  rules: string[];
  benefits: string[];
  pointTypes?: {
    automatic: {
      title: string;
      description: string;
      examples: string[];
    };
    approval: {
      title: string;
      description: string;
      examples: string[];
    };
  };
}

const ModernPointEarning: React.FC = () => {
  const { user } = useUserStore();
  const { notify } = useNotification();
  const { translate } = useTranslation();
  const [showSystemInfo, setShowSystemInfo] = useState(false);
  const [selectedTask, setSelectedTask] = useState<PointTask | null>(null);

  // Point system information
  const pointSystemInfo: PointSystemInfo = {
    title: translate(
      'pos.staff.point_earning.system_title',
      'Neko Point System'
    ),
    description: translate(
      'pos.staff.point_earning.system_description',
      'Earn points by completing daily tasks and achieving milestones. Points can be redeemed for rewards and recognition.'
    ),
    rules: [
      translate(
        'pos.staff.point_earning.rule_complete_tasks',
        'Complete tasks to earn points'
      ),
      translate(
        'pos.staff.point_earning.rule_difficulty_based',
        'Points are awarded based on task difficulty'
      ),
      translate(
        'pos.staff.point_earning.rule_daily_reset',
        'Daily tasks reset every 24 hours'
      ),
      translate(
        'pos.staff.point_earning.rule_bonus_challenges',
        'Weekly and monthly challenges offer bonus points'
      ),
      translate(
        'pos.staff.point_earning.rule_redeem',
        'Points can be redeemed in the Point Store'
      ),
    ],
    benefits: [
      translate(
        'pos.staff.point_earning.benefit_redeem',
        'Redeem points for rewards'
      ),
      translate(
        'pos.staff.point_earning.benefit_badges',
        'Unlock achievement badges'
      ),
      translate(
        'pos.staff.point_earning.benefit_leaderboard',
        'Climb the leaderboard rankings'
      ),
      translate(
        'pos.staff.point_earning.benefit_recognition',
        'Earn recognition from management'
      ),
      translate(
        'pos.staff.point_earning.benefit_perks',
        'Access exclusive perks and benefits'
      ),
    ],
    pointTypes: {
      automatic: {
        title: translate(
          'pos.staff.point_earning.types_automatic_title',
          'Automatic Points'
        ),
        description: translate(
          'pos.staff.point_earning.types_automatic_description',
          'Points are awarded immediately when you complete the task. No approval needed - based on verifiable system data.'
        ),
        examples: [
          translate(
            'pos.staff.point_earning.types_automatic_example_1',
            'Early check-in (verified by system timestamp)'
          ),
          translate(
            'pos.staff.point_earning.types_automatic_example_2',
            'Daily sales targets (verified by sales data)'
          ),
          translate(
            'pos.staff.point_earning.types_automatic_example_3',
            'Perfect attendance (verified by attendance records)'
          ),
        ],
      },
      approval: {
        title: translate(
          'pos.staff.point_earning.types_approval_title',
          'Approval-Required Points'
        ),
        description: translate(
          'pos.staff.point_earning.types_approval_description',
          'Points require manager verification before being awarded. Submit your completion and wait for approval.'
        ),
        examples: [
          translate(
            'pos.staff.point_earning.types_approval_example_1',
            'Excellent customer service (requires verification of feedback)'
          ),
          translate(
            'pos.staff.point_earning.types_approval_example_2',
            'Team collaboration (requires verification from colleagues)'
          ),
          translate(
            'pos.staff.point_earning.types_approval_example_3',
            'Skill development (requires verification of training completion)'
          ),
        ],
      },
    },
  };

  // Sample tasks data
  const [tasks, setTasks] = useState<PointTask[]>([
    {
      id: 'checkin-early',
      title: 'Early Check-in',
      description: 'Check in 15 minutes before your shift starts',
      points: 10,
      category: 'Attendance',
      difficulty: 'easy',
      icon: Clock,
      completed: false,
      progress: 0,
      maxProgress: 1,
      requiresApproval: false, // Automatic - no approval needed
      explanation:
        'Arrive at work 15 minutes before your scheduled shift time and check in through the system. This shows punctuality and dedication.',
    },
    {
      id: 'customer-service',
      title: 'Excellent Customer Service',
      description: 'Receive 5 positive customer feedbacks in a day',
      points: 25,
      category: 'Customer Service',
      difficulty: 'medium',
      icon: Heart,
      completed: false,
      progress: 2,
      maxProgress: 5,
      requiresApproval: true, // Needs manager verification
      explanation:
        'Provide exceptional service to customers and collect positive feedback. Aim for 5 positive reviews in a single day. Points will be awarded after manager verification.',
    },
    {
      id: 'sales-target',
      title: 'Daily Sales Target',
      description: 'Achieve 120% of your daily sales target',
      points: 50,
      category: 'Sales',
      difficulty: 'hard',
      icon: Target,
      completed: false,
      progress: 85,
      maxProgress: 100,
      requiresApproval: false, // Automatic based on sales data
      explanation:
        'Exceed your daily sales target by 20%. Focus on upselling, cross-selling, and providing excellent customer service to boost sales.',
    },
    {
      id: 'team-collaboration',
      title: 'Team Player',
      description: 'Help 3 colleagues with their tasks today',
      points: 15,
      category: 'Teamwork',
      difficulty: 'easy',
      icon: Users,
      completed: false,
      progress: 1,
      maxProgress: 3,
      requiresApproval: true, // Needs verification from colleagues/manager
      explanation:
        'Support your teammates by helping them with their tasks. This could include training, covering shifts, or assisting with difficult customers. Points require manager verification.',
    },
    {
      id: 'clean-station',
      title: 'Station Maintenance',
      description: 'Keep your work station clean and organized',
      points: 5,
      category: 'Maintenance',
      difficulty: 'easy',
      icon: Shield,
      completed: true,
      progress: 1,
      maxProgress: 1,
      requiresApproval: false, // Automatic
      explanation:
        'Maintain a clean, organized, and professional work station throughout your shift. This includes cleaning equipment, organizing supplies, and following safety protocols.',
    },
    {
      id: 'upsell-coffee',
      title: 'Coffee Specialist',
      description: 'Upsell premium coffee to 10 customers',
      points: 30,
      category: 'Sales',
      difficulty: 'medium',
      icon: Coffee,
      completed: false,
      progress: 6,
      maxProgress: 10,
      requiresApproval: false, // Automatic based on sales data
      explanation:
        'Promote our premium coffee options to customers. Explain the benefits, offer samples, and help customers discover new flavors they might enjoy.',
    },
    {
      id: 'perfect-attendance',
      title: 'Perfect Week',
      description: 'Complete all scheduled shifts without being late',
      points: 100,
      category: 'Attendance',
      difficulty: 'hard',
      icon: Award,
      completed: false,
      progress: 4,
      maxProgress: 5,
      requiresApproval: false, // Automatic based on attendance records
      explanation:
        'Maintain perfect attendance for the entire week. This means no late arrivals, no early departures, and completing all scheduled shifts.',
    },
    {
      id: 'training-complete',
      title: 'Skill Development',
      description: 'Complete a training module or certification',
      points: 40,
      category: 'Development',
      difficulty: 'medium',
      icon: Trophy,
      completed: false,
      progress: 0,
      maxProgress: 1,
      requiresApproval: true, // Needs verification of completion
      explanation:
        'Take initiative to improve your skills by completing training modules, certifications, or learning new techniques that benefit your work performance. Points require manager verification.',
    },
  ]);

  const handleTaskClick = (task: PointTask) => {
    if (task.completed) {
      notify(
        translate(
          'pos.staff.point_earning.task_already_completed',
          'Task already completed!'
        ),
        { type: 'success' }
      );
      return;
    }

    // Check if task requires approval
    if (task.requiresApproval) {
      // For tasks requiring approval, mark as pending
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? {
                ...t,
                completed: false,
                approvalStatus: 'pending',
                progress: t.maxProgress || 1,
              }
            : t
        )
      );

      notify(
        translate(
          'pos.staff.point_earning.task_submitted_for_approval',
          'Task submitted for approval! Points will be awarded after manager verification.'
        ),
        { type: 'info' }
      );
    } else {
      // For automatic tasks, mark as completed immediately
      setTasks((prev) =>
        prev.map((t) =>
          t.id === task.id
            ? { ...t, completed: true, progress: t.maxProgress || 1 }
            : t
        )
      );

      notify(
        translate(
          'pos.staff.point_earning.task_completed_message',
          'Task completed! You earned %{points} points!'
        ).replace('%{points}', `${task.points}`),
        { type: 'success' }
      );
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'hard':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return translate('pos.staff.point_earning.difficulty_easy', 'Easy');
      case 'medium':
        return translate(
          'pos.staff.point_earning.difficulty_medium',
          'Medium'
        );
      case 'hard':
        return translate('pos.staff.point_earning.difficulty_hard', 'Hard');
      default:
        return difficulty;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Attendance':
        return Clock;
      case 'Customer Service':
        return Heart;
      case 'Sales':
        return Target;
      case 'Teamwork':
        return Users;
      case 'Maintenance':
        return Shield;
      case 'Development':
        return Trophy;
      default:
        return Star;
    }
  };

  const totalPoints = tasks.reduce(
    (sum, task) => sum + (task.completed ? task.points : 0),
    0
  );
  const completedTasks = tasks.filter((task) => task.completed).length;
  const totalTasks = tasks.length;

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {translate('pos.staff.point_earning.title', 'Point Earning')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {translate(
              'pos.staff.point_earning.subtitle',
              'Complete tasks to earn points and unlock rewards'
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSystemInfo(true)}
          className="flex items-center gap-2"
        >
          <Info className="h-4 w-4" />
          {translate(
            'pos.staff.point_earning.how_points_work',
            'How Points Work'
          )}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.staff.point_earning.total_points', 'Total Points')}
            </CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalPoints}</div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.point_earning.points_earned_today',
                'Points earned today'
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate(
                'pos.staff.point_earning.tasks_completed',
                'Tasks Completed'
              )}
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {completedTasks}/{totalTasks}
            </div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.point_earning.tasks_completed_today',
                'Tasks completed today'
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.staff.point_earning.progress', 'Progress')}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round((completedTasks / totalTasks) * 100)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.point_earning.daily_completion_rate',
                'Daily completion rate'
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tasks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tasks.map((task) => {
          const CategoryIcon = getCategoryIcon(task.category);
          const TaskIcon = task.icon;
          const progressPercentage = task.maxProgress
            ? ((task.progress || 0) / task.maxProgress) * 100
            : 0;

          return (
            <Card
              key={task.id}
              className={`relative ${
                task.completed ? 'bg-green-50 border-green-200' : ''
              }`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        task.completed ? 'bg-green-100' : 'bg-blue-100'
                      }`}
                    >
                      <TaskIcon
                        className={`h-5 w-5 ${
                          task.completed ? 'text-green-600' : 'text-blue-600'
                        }`}
                      />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{task.title}</CardTitle>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge className={getDifficultyColor(task.difficulty)}>
                          {getDifficultyLabel(task.difficulty)}
                        </Badge>
                        <Badge
                          variant="outline"
                          className="flex items-center gap-1"
                        >
                          <CategoryIcon className="h-3 w-3" />
                          {task.category}
                        </Badge>
                        {task.requiresApproval && (
                          <Badge
                            variant="outline"
                            className={
                              task.approvalStatus === 'approved'
                                ? 'bg-green-100 text-green-800 border-green-300'
                                : task.approvalStatus === 'rejected'
                                ? 'bg-red-100 text-red-800 border-red-300'
                                : task.approvalStatus === 'pending'
                                ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                                : 'bg-blue-100 text-blue-800 border-blue-300'
                            }
                          >
                            {task.approvalStatus === 'approved'
                              ? translate(
                                  'pos.staff.point_earning.approved',
                                  'Approved'
                                )
                              : task.approvalStatus === 'rejected'
                              ? translate(
                                  'pos.staff.point_earning.rejected',
                                  'Rejected'
                                )
                              : task.approvalStatus === 'pending'
                              ? translate(
                                  'pos.staff.point_earning.pending_approval',
                                  'Pending Approval'
                                )
                              : translate(
                                  'pos.staff.point_earning.requires_approval',
                                  'Requires Approval'
                                )}
                          </Badge>
                        )}
                        {!task.requiresApproval && (
                          <Badge
                            variant="outline"
                            className="bg-green-100 text-green-800 border-green-300"
                          >
                            {translate(
                              'pos.staff.point_earning.automatic',
                              'Automatic'
                            )}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedTask(task)}
                    className="h-8 w-8 p-0"
                  >
                    <Info className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {task.description}
                </p>

                {/* Progress Bar */}
                {task.maxProgress && task.maxProgress > 1 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>
                        {translate(
                          'pos.staff.point_earning.progress_label',
                          'Progress'
                        )}
                      </span>
                      <span>
                        {task.progress || 0}/{task.maxProgress}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progressPercentage}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Points and Action */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    <span className="font-semibold text-lg">
                      {task.points}{' '}
                      {translate('pos.staff.point_earning.points_label', 'pts')}
                    </span>
                  </div>

                  <Button
                    onClick={() => handleTaskClick(task)}
                    disabled={
                      task.completed ||
                      task.approvalStatus === 'pending' ||
                      task.approvalStatus === 'approved'
                    }
                    size="sm"
                    className={
                      task.completed || task.approvalStatus === 'approved'
                        ? 'bg-green-600 hover:bg-green-700'
                        : task.approvalStatus === 'pending'
                        ? 'bg-yellow-600 hover:bg-yellow-700'
                        : task.approvalStatus === 'rejected'
                        ? 'bg-red-600 hover:bg-red-700'
                        : ''
                    }
                  >
                    {task.completed || task.approvalStatus === 'approved' ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {translate(
                          'pos.staff.point_earning.completed',
                          'Completed'
                        )}
                      </>
                    ) : task.approvalStatus === 'pending' ? (
                      <>
                        <Clock className="h-4 w-4 mr-2" />
                        {translate(
                          'pos.staff.point_earning.pending',
                          'Pending'
                        )}
                      </>
                    ) : task.approvalStatus === 'rejected' ? (
                      translate(
                        'pos.staff.point_earning.rejected',
                        'Rejected'
                      )
                    ) : (
                      translate(
                        'pos.staff.point_earning.complete_task',
                        'Complete Task'
                      )
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* System Info Dialog */}
      {showSystemInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                {pointSystemInfo.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                {pointSystemInfo.description}
              </p>

              <div>
                <h4 className="font-semibold mb-2">
                  {translate(
                    'pos.staff.point_earning.how_it_works',
                    'How It Works:'
                  )}
                </h4>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {pointSystemInfo.rules.map((rule, index) => (
                    <li key={index}>{rule}</li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">
                  {translate(
                    'pos.staff.point_earning.benefits_title',
                    'Benefits:'
                  )}
                </h4>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {pointSystemInfo.benefits.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </div>

              {/* Point Types Explanation */}
              {pointSystemInfo.pointTypes && (
                <div className="space-y-4 border-t pt-4">
                  <h4 className="font-semibold mb-3">
                    {translate(
                      'pos.staff.point_earning.types_title',
                      'Types of Point Earning:'
                    )}
                  </h4>

                  {/* Automatic Points */}
                  <Card className="bg-green-50 border-green-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        {pointSystemInfo.pointTypes.automatic.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        {pointSystemInfo.pointTypes.automatic.description}
                      </p>
                      <div className="text-sm">
                        <p className="font-medium mb-1">
                          {translate(
                            'pos.staff.point_earning.types_examples',
                            'Examples:'
                          )}
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                          {pointSystemInfo.pointTypes.automatic.examples.map(
                            (example, index) => (
                              <li key={index}>{example}</li>
                            )
                          )}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Approval-Required Points */}
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Clock className="h-4 w-4 text-blue-600" />
                        {pointSystemInfo.pointTypes.approval.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        {pointSystemInfo.pointTypes.approval.description}
                      </p>
                      <div className="text-sm">
                        <p className="font-medium mb-1">
                          {translate(
                            'pos.staff.point_earning.types_examples',
                            'Examples:'
                          )}
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                          {pointSystemInfo.pointTypes.approval.examples.map(
                            (example, index) => (
                              <li key={index}>{example}</li>
                            )
                          )}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={() => setShowSystemInfo(false)}>
                  {translate('pos.staff.point_earning.got_it', 'Got it!')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Task Info Dialog */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                {selectedTask.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={getDifficultyColor(selectedTask.difficulty)}>
                  {getDifficultyLabel(selectedTask.difficulty)}
                </Badge>
                <Badge variant="outline">{selectedTask.category}</Badge>
                {selectedTask.requiresApproval && (
                  <Badge
                    variant="outline"
                    className={
                      selectedTask.approvalStatus === 'approved'
                        ? 'bg-green-100 text-green-800 border-green-300'
                        : selectedTask.approvalStatus === 'rejected'
                        ? 'bg-red-100 text-red-800 border-red-300'
                        : selectedTask.approvalStatus === 'pending'
                        ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                        : 'bg-blue-100 text-blue-800 border-blue-300'
                    }
                  >
                    {selectedTask.approvalStatus === 'approved'
                      ? translate(
                          'pos.staff.point_earning.approved',
                          'Approved'
                        )
                      : selectedTask.approvalStatus === 'rejected'
                      ? translate(
                          'pos.staff.point_earning.rejected',
                          'Rejected'
                        )
                      : selectedTask.approvalStatus === 'pending'
                      ? translate(
                          'pos.staff.point_earning.pending_approval',
                          'Pending Approval'
                        )
                      : translate(
                          'pos.staff.point_earning.requires_approval',
                          'Requires Approval'
                        )}
                  </Badge>
                )}
                {!selectedTask.requiresApproval && (
                  <Badge
                    variant="outline"
                    className="bg-green-100 text-green-800 border-green-300"
                  >
                    {translate(
                      'pos.staff.point_earning.automatic',
                      'Automatic'
                    )}
                  </Badge>
                )}
                <div className="flex items-center gap-1 ml-auto">
                  <Star className="h-4 w-4 text-yellow-500" />
                  <span className="font-semibold">
                    {selectedTask.points}{' '}
                    {translate('pos.staff.point_earning.points', 'points')}
                  </span>
                </div>
              </div>

              {selectedTask.requiresApproval && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    {translate(
                      'pos.staff.point_earning.approval_notice',
                      'This task requires manager approval. Points will be awarded after verification.'
                    )}
                  </p>
                </div>
              )}
              {!selectedTask.requiresApproval && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    {translate(
                      'pos.staff.point_earning.automatic_notice',
                      'This task awards points automatically upon completion.'
                    )}
                  </p>
                </div>
              )}

              <p className="text-muted-foreground">
                {selectedTask.description}
              </p>

              <div>
                <h4 className="font-semibold mb-2">
                  {translate(
                    'pos.staff.point_earning.how_to_complete',
                    'How to Complete:'
                  )}
                </h4>
                <p className="text-sm">{selectedTask.explanation}</p>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => setSelectedTask(null)}>
                  {translate('pos.staff.point_earning.close', 'Close')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ModernPointEarning;
